import {
    FetchState,
    FetchStateKey,
    PersonalContributionSchedulesState,
    TransferSchedule,
    FutureContribution,
    TransferScheduleType,
    TransferScheduleStatus
} from "@/shared/types";
import { fetchStateKeyMatch } from "@/shared/fetchStateHelper";
import * as BankService from "@/shared/api/bankService";
import * as storeHelper from "@/shared/storeHelper";
import moment from "moment";
import { formatMMMDateString } from "@/shared/dateString";
import { defineStore } from "pinia";
import _ from "lodash";
import { isScheduleCancelledOrCompleted } from "@/shared/components/contributionsV3/scheduleStatusHelper";
import { useBankHolidayStore } from "@/employer/store/bankHoliday";
import { useUserStore } from "./user";

const pageSize = 10;

const computeFutureContributionEffectiveYear = (src: FutureContribution): number => {
    return src.effectiveYear ?
        src.effectiveYear :
        moment(src.calendarDate).year() - (src.isPriorYearContribution ? 1 : 0);
};

function getSchedules (state: PersonalContributionSchedulesState)
    : TransferSchedule[] {
    if (state.personalContributionSchedules.fetchState !== FetchState.Loaded) {
        return [];
    }

    return state.personalContributionSchedules.schedules;
}

function paginateSchedules (state: PersonalContributionSchedulesState)
    : TransferSchedule[] {
    return getSchedules(state)
        .sort((a, b) => {
            if (!isScheduleCancelledOrCompleted(a) && isScheduleCancelledOrCompleted(b)) {
                return -1;
            }
            else if (isScheduleCancelledOrCompleted(a) && !isScheduleCancelledOrCompleted(b)) {
                return 1;
            }
            else if (!isScheduleCancelledOrCompleted(a) && !isScheduleCancelledOrCompleted(b)) {
                return activeSchedulesDateSort(a, b);
            }
            else if (isScheduleCancelledOrCompleted(a) && isScheduleCancelledOrCompleted(b)) {
                return completedSchedulesDateSort(a, b);
            }
            return 0;
        })
        .slice((state.personalContributionSchedules.pageNumber - 1) * pageSize,
            state.personalContributionSchedules.pageNumber * pageSize)
        .map(s => {
          return { ...s,
              upcomingDates: getUpcomingContributions(s.scheduleId, state),
              nextDate: getNextDate(s, state)
          };
        });
}

function getUpcomingContributions (scheduleId: number, state: PersonalContributionSchedulesState): string[] {
    return _.sortBy(state.futureContributions.contributions
      .filter(f => f.personalContributionScheduleId === scheduleId), "calendarDate")
      .slice(0, 3)
      .map(m => formatMMMDateString(m.calendarDate));
}

function getNextDate (schedule: TransferSchedule, state: PersonalContributionSchedulesState): string {
    if (schedule.scheduleType === TransferScheduleType.OneTime) {
        return formatMMMDateString(schedule.scheduleStartDate);
    }

    if (isScheduleCancelledOrCompleted(schedule)) {
        return "N/A";
    }
    const nextContribution = state.futureContributions.contributions.find(
      x => x.personalContributionScheduleId === schedule.scheduleId);
    if (!nextContribution) {
        return "N/A";
    }

    return nextContribution && formatMMMDateString(nextContribution.calendarDate) || ">1 Year";
}

function activeSchedulesDateSort (a: TransferSchedule, b: TransferSchedule): number {
    return moment(a.scheduleStartDate).isSame((moment(b.scheduleStartDate))) ? 0
        : moment(a.scheduleStartDate).isAfter(moment(b.scheduleStartDate)) ? -1 : 1;
}

function completedSchedulesDateSort (a: TransferSchedule, b: TransferSchedule): number {
    const aDate = getCompletedOrCancelledEndDate(a);
    const bDate = getCompletedOrCancelledEndDate(b);
    return moment(aDate).isSame((moment(bDate))) ? 0
        : moment(aDate).isAfter(moment(bDate)) ? -1 : 1;
}

function getCompletedOrCancelledEndDate (schedule: TransferSchedule) {
    if (schedule.status === TransferScheduleStatus.Completed) {
         return schedule.scheduleType === TransferScheduleType.OneTime ? schedule.scheduleStartDate : schedule.scheduleEndDate;
    }
    // cancelled
    return schedule.dateLastModified;
}

function getPageCount (state: PersonalContributionSchedulesState): number {
    return Math.max(Math.ceil(getSchedules(state).length / pageSize), 1);
}

function getProposedContributionsPageCount (state: PersonalContributionSchedulesState): number {
    return Math.max(Math.ceil(state.proposedPersonalContributionScheduleCalendars.calendarEntries.length / pageSize), 1);
}

function paginateProposedContributions (state: PersonalContributionSchedulesState, page: number): FutureContribution[] {
    return state.proposedPersonalContributionScheduleCalendars.calendarEntries
        .slice((page - 1) * pageSize, page * pageSize);
}

function getFederalHolidays (year: number): string[] {
    const bankHolidayStore = useBankHolidayStore();
    return [...bankHolidayStore.bankHolidaysInYear(year), ...bankHolidayStore.bankHolidaysInYear(year  + 1)];
}

export const initialState: PersonalContributionSchedulesState = {
    personalContributionSchedules: {
        fetchState: FetchState.Uninitialized,
        fetchKey: null,
        pageNumber: 1,
        schedules: null,
    },
    futureContributions: {
        fetchState: FetchState.Uninitialized,
        fetchKey: null,
        contributions: [],
    },
    upcomingTotalContributions: {
        fetchState: FetchState.Uninitialized,
        upcomingContributionsTotal: 0,
    },
    proposedPersonalContributionScheduleCalendars: {
        fetchState: FetchState.Uninitialized,
        pageNumber: 1,
        calendarEntries: []
    },
    personalContributionSchedulingEligibility: {
        fetchState: FetchState.Uninitialized,
        isEligible: null
    },
    schedule: null,
    scheduleFetchState: FetchState.Uninitialized
};

export const usePersonalContributionSchedulesStore = defineStore("personalContributionSchedules", {
    state: (): PersonalContributionSchedulesState => (_.cloneDeep(initialState)),
    getters: {
        isScheduleNotFound: state => state.scheduleFetchState === FetchState.Failed,
        isScheduleLoaded: state => state.scheduleFetchState === FetchState.Loaded,
        schedulesLoaded: state => state.personalContributionSchedules.fetchState === FetchState.Loaded,
        schedulesLoading: state => state.personalContributionSchedules.fetchState === FetchState.Loading,
        schedulesPages: state => getPageCount(state),
        schedulesForPage: state => paginateSchedules(state),
        isFutureContributionsLoaded: state => state.futureContributions.fetchState === FetchState.Loaded,
        futureContributionsLoading: state => state.futureContributions.fetchState === FetchState.Loading,
        allFutureContributions: state => state.futureContributions.contributions,
        upcomingContributionsLoading: state => state.upcomingTotalContributions.fetchState === FetchState.Loading,
        upcomingContributionsLoaded: state => state.upcomingTotalContributions.fetchState === FetchState.Loaded,
        allUpcomingContributions: state => state.upcomingTotalContributions.upcomingContributionsTotal,
        allProposedPersonalContributionScheduleCalendars: state =>
            state.proposedPersonalContributionScheduleCalendars.calendarEntries,
        proposedPersonalContributionScheduleCalendarsLoading: state =>
            state.proposedPersonalContributionScheduleCalendars.fetchState === FetchState.Loading,
        proposedPersonalContributionScheduleCalendarsLoaded: state =>
            state.proposedPersonalContributionScheduleCalendars.fetchState === FetchState.Loaded,
        proposedContributionsPageCount: state => getProposedContributionsPageCount(state),
        proposedContributionsPage: state => (page: number) => paginateProposedContributions(state, page),
        isContributionSchedulingEligibilityLoading: state =>
            state.personalContributionSchedulingEligibility.fetchState === FetchState.Loading,
        isUserEligibleToScheduleContribution: state => state.personalContributionSchedulingEligibility.isEligible,
        nextProposedPersonalContributionScheduleCalendars () {
            return (numberOfEntries: number) => {
                if (this.proposedPersonalContributionScheduleCalendars.fetchState !== FetchState.Loaded) {
                    return [];
                }

                return this.allProposedPersonalContributionScheduleCalendars.slice(0, numberOfEntries);
            };
        },
        proposedPersonalContributionScheduleCalendarsForYear () {
            return (year: number) => {
                if (this.proposedPersonalContributionScheduleCalendars.fetchState !== FetchState.Loaded) {
                    return [];
                }

                return this.allProposedPersonalContributionScheduleCalendars
                    .filter(x => moment(x.calendarDate).year() === year);
            };
        }
    },
    actions: {
        invalidateSchedulesFetchState (key: FetchStateKey) {
            if (fetchStateKeyMatch(key, this.personalContributionSchedules.fetchKey)) {
                return;
            }

            this.personalContributionSchedules.schedules = null;
            this.personalContributionSchedules.fetchKey = null;
            this.personalContributionSchedules.fetchState = FetchState.Uninitialized;
        },
        resetSchedulesFetchState () {
            this.personalContributionSchedules.schedules = null;
            this.personalContributionSchedules.fetchKey = null;
            this.personalContributionSchedules.fetchState = FetchState.Uninitialized;
            this.setUpcomingTotalContributionScheduleForYearFetchState(FetchState.Uninitialized);
        },
        setSchedulesFetchState ({ fetchState, key }: { fetchState: FetchState, key: FetchStateKey }) {
            this.personalContributionSchedules.fetchState = fetchState;
            this.personalContributionSchedules.fetchKey = key;
        },
        setPersonalContributionSchedules (personalContributionSchedules: TransferSchedule[]) {
            this.personalContributionSchedules.schedules = personalContributionSchedules;
        },
        setSchedulesPage (page: number) {
            this.personalContributionSchedules.pageNumber = page;
        },
        setFutureContributionsFetchState ({ fetchState, fetchKey }: {
            fetchState: FetchState, fetchKey: FetchStateKey
        }) {
            if (this.futureContributions.fetchState === FetchState.Uninitialized) {
                this.futureContributions.fetchKey = fetchKey;
                this.futureContributions.fetchState = fetchState;
                return;
            }
            if (fetchStateKeyMatch(this.futureContributions.fetchKey, fetchKey)) {
                this.futureContributions.fetchState = fetchState;
            }
        },
        setFutureContributions (contributions: FutureContribution[]) {
            this.futureContributions.contributions = contributions;
        },
        setUpcomingTotalContributionScheduleForYearFetchState (fetchState: FetchState) {
            this.upcomingTotalContributions.fetchState = fetchState;
        },
        setUpcomingTotalContributionScheduleForYear (contributionsTotal: number) {
            this.upcomingTotalContributions.upcomingContributionsTotal = contributionsTotal;
        },
        invalidateFutureContributionsState (key: FetchStateKey) {
            if (fetchStateKeyMatch(key, this.futureContributions.fetchKey)) {
                return;
            }
            this.futureContributions.contributions = [];
            this.futureContributions.fetchState = FetchState.Uninitialized;
            this.futureContributions.fetchKey = null;
        },
        invalidateProposedPersonalContributionScheduleCalendarsState () {
            this.proposedPersonalContributionScheduleCalendars.fetchState = FetchState.Uninitialized;
            this.proposedPersonalContributionScheduleCalendars.calendarEntries = [];
        },
        setProposedPersonalContributionScheduleCalendarsFetchState (fetchState: FetchState) {
            this.proposedPersonalContributionScheduleCalendars.fetchState = fetchState;
        },
        setProposedPersonalContributionScheduleCalendars (contributions: FutureContribution[]) {
            this.proposedPersonalContributionScheduleCalendars.calendarEntries = contributions;
        },
        invalidateContributionSchedulingEligibilityState () {
            this.personalContributionSchedulingEligibility.fetchState = FetchState.Uninitialized;
            this.personalContributionSchedulingEligibility.isEligible = null;
        },
        setContributionSchedulingEligibilityFetchState (fetchState: FetchState) {
            this.personalContributionSchedulingEligibility.fetchState = fetchState;
        },
        setContributionSchedulingEligibility (isEligible: boolean) {
            this.personalContributionSchedulingEligibility.isEligible = isEligible;
        },
        getPersonalContributionSchedules (args: { username?: string, partnerId?: number }): Promise<void> {
            args = args || {};
            const fetchKey: FetchStateKey = {
                username: args.username,
                partnerId: args.partnerId
            };

            this.invalidateSchedulesFetchState(fetchKey);
            if (this.personalContributionSchedules.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setSchedulesFetchState({ fetchState: FetchState.Loading, key: fetchKey });

            return fetchPersonalContributionSchedules(args.username, args.partnerId).then(resp => {
                this.setPersonalContributionSchedules(resp.data.map(x => {
        return {
          ...x,
          scheduleStartDate: x.scheduleStartDate.substring(0, 10),
          scheduleEndDate: x.scheduleEndDate && x.scheduleEndDate.substring(0, 10)
        };
      }));
                this.setSchedulesFetchState({ fetchState: FetchState.Loaded, key: fetchKey });
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: false });
            });
        },
        getContributionSchedule (scheduleId: number): Promise<void> {
            this.scheduleFetchState = FetchState.Loading;

            return BankService.getPersonalContributionSchedule(scheduleId)
              .then(resp => {
                this.schedule = resp.data;
                this.scheduleFetchState = FetchState.Loaded;
              }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: false });
                this.scheduleFetchState = FetchState.Failed;
            });
        },
        createPersonalContributionSchedule (request: TransferSchedule): Promise<void> {
            return BankService.addPersonalContributionSchedule(request).then(() => {
                // Force a reload of schedules and future contributions to get new changes
                this.setSchedulesFetchState(
                    { fetchState: FetchState.Uninitialized, key: this.personalContributionSchedules.fetchKey });
                this.setFutureContributionsFetchState(
                    { fetchState: FetchState.Uninitialized, key: this.futureContributions.fetchKey });
            }).catch(err => {
                return storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        updatePersonalContributionSchedule (request: TransferSchedule): Promise<void> {
            return BankService.updatePersonalContributionSchedule(request).then(() => {
                // Force a reload of schedules to catch the update
                this.setSchedulesFetchState(
                    { fetchState: FetchState.Uninitialized, key: this.personalContributionSchedules.fetchKey });
                this.setFutureContributionsFetchState(
                    { fetchState: FetchState.Uninitialized, key: this.futureContributions.fetchKey });
            }).catch(err => {
                return storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        cancelPersonalContributionSchedule (scheduleId: number, username?: string, partnerId?: number): Promise<void> {
            return bankCancelPersonalContributionSchedule(scheduleId, username, partnerId).then(() => {
                // Force a reload of schedules to catch the update
                this.setSchedulesFetchState(
                  { fetchState: FetchState.Uninitialized, key: this.personalContributionSchedules.fetchKey });
                this.setFutureContributionsFetchState(
                  { fetchState: FetchState.Uninitialized, key: this.futureContributions.fetchKey });
            }).catch(err => {
                return storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        getFutureContributions (args: {
            username?: string,
            partnerId?: number
        }): Promise<any> {
            args = args || {};
            const fetchKey: FetchStateKey = {
                username: args.username,
                partnerId: args.partnerId
            };

            this.invalidateFutureContributionsState(fetchKey);

            if (this.futureContributions.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setFutureContributionsFetchState({ fetchKey, fetchState: FetchState.Loading });
            let currentDate = moment().add(1, "days"); // We want contributions starting tomorrow
            if (moment().hour() >= 14) { // If its 2pm, tomorrow's contribution is pending so we skip another day
                currentDate = currentDate.add(1, "days");
            }
            return fetchFutureContributions(args.username,
                    args.partnerId,
                    currentDate.format("YYYY-MM-DD"),
                    moment().add(1, "years").format("YYYY-MM-DD"))
                .then(result => {
                    result.data = result.data.map(s => ({ ...s, effectiveYear: computeFutureContributionEffectiveYear(s) }));
                    this.setFutureContributions(result.data);
                    this.setFutureContributionsFetchState({ fetchKey, fetchState: FetchState.Loaded });
                }).catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: false });
                });
        },

        getProposedPersonalContributionScheduleCalendars (args: {
            proposedSchedule: TransferSchedule,
            toDate: string,
            numberOfCalendarEntries: number
        }): Promise<any> {
            this.invalidateProposedPersonalContributionScheduleCalendarsState();
            this.setProposedPersonalContributionScheduleCalendarsFetchState(FetchState.Loading);

            return BankService.getProposedPersonalContributionScheduleCalendarsToDate(
                args.proposedSchedule, args.toDate)
                .then(result => {
                    // Business requirement is at least 3 dates,
                    // so if the forecast doesn't have enough entries, we need to request more
                    if (result.data.length < args.numberOfCalendarEntries) {
                        return BankService.getProposedPersonalContributionScheduleCalendars(
                            args.proposedSchedule, args.numberOfCalendarEntries);
                    }

                    return result;
                })
                .then(result => {
                    this.setProposedPersonalContributionScheduleCalendars(result.data);
                    this.setProposedPersonalContributionScheduleCalendarsFetchState(FetchState.Loaded);
                }).catch(err => {
                    this.invalidateProposedPersonalContributionScheduleCalendarsState();
                    return storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        getPersonalContributionSchedulingEligibility (): Promise<any> {
            this.invalidateContributionSchedulingEligibilityState();

            if (this.personalContributionSchedulingEligibility.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setContributionSchedulingEligibilityFetchState(FetchState.Loading);

            return BankService.getPersonalContributionSchedulingEligibility()
                .then(result => {
                    this.setContributionSchedulingEligibility(result.data);
                    this.setContributionSchedulingEligibilityFetchState(FetchState.Loaded);
                }).catch(err => {
                    this.invalidateContributionSchedulingEligibilityState();
                    return storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        getUpcomingPersonalContributionTotal (selectedYear: number, args?: { username: string, partnerId: number }): Promise<void> {
            this.setUpcomingTotalContributionScheduleForYearFetchState(FetchState.Loading);

            return BankService.getPersonalProjectedContributionsForYear(selectedYear, args)
                .then(result => {
                     this.setUpcomingTotalContributionScheduleForYear(result.data);
                     this.setUpcomingTotalContributionScheduleForYearFetchState(FetchState.Loaded);
                }).catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: false });
                });
        }
    }
});

function fetchPersonalContributionSchedules (username?: string, partnerId?: number) {
    const userStore = useUserStore();
    return userStore.isAdminPortal
        ? BankService.getPersonalContributionSchedulesAdmin(username, partnerId)
        : BankService.getPersonalContributionSchedules(username, partnerId);
}

function bankCancelPersonalContributionSchedule (scheduleId: number, username?: string, partnerId?: number) {
    const userStore = useUserStore();
    return userStore.isAdminPortal
        ? BankService.cancelPersonalContributionScheduleAdmin(scheduleId, username, partnerId)
        : BankService.cancelPersonalContributionSchedule(scheduleId, username, partnerId);
}

function fetchFutureContributions (username: string, partnerId: number, startDate: string, endDate: string) {
    const userStore = useUserStore();
    return userStore.isAdminPortal
        ? BankService.getFutureContributionsAdmin(username, partnerId, startDate, endDate)
        : BankService.getFutureContributions(username, partnerId, startDate, endDate);
}